import { useCallback, useEffect, useState } from 'react'
import { COMPAT_ANCHOR, WAITLIST_PATH } from '@/lib/landing'
import { actionClass } from './primitives'

/**
 * "Will it run on my Mac?" — answered from what a browser can honestly see.
 * macOS: user agent. Apple Silicon: the WebGL renderer string names the chip
 * in Chromium ("Apple M2 Pro"); Safari masks it, so we say so instead of
 * guessing. Memory: Chromium exposes deviceMemory (capped at 8), others none.
 */
type Verdict = 'ready' | 'probably' | 'no'
interface Report {
  verdict: Verdict
  isMac: boolean
  chip: string | null
  cores: number | null
  /** Lower bound only: from the chip tier when known, else the browser's capped figure. */
  minMemoryGb: number | null
  memorySource: 'chip' | 'browser' | null
}

/**
 * Browsers never expose real memory (Chromium caps deviceMemory at 8), but
 * Apple's configurations give a floor per chip tier: no Max has shipped with
 * less than 32 GB, no Pro with less than 16, and so on.
 */
function minMemoryForChip(chip: string): number | null {
  const m = chip.match(/M(\d+)(?:\s+(Pro|Max|Ultra))?/i)
  if (!m) return null
  const gen = Number(m[1])
  const tier = (m[2] ?? '').toLowerCase()
  if (tier === 'ultra') return 64
  if (tier === 'max') return gen <= 2 ? 32 : 36
  if (tier === 'pro') return gen <= 2 ? 16 : gen === 3 ? 18 : 24
  return gen <= 3 ? 8 : 16
}

function inspect(): Report {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string }; deviceMemory?: number }
  const platform = nav.userAgentData?.platform ?? nav.platform ?? ''
  const ua = nav.userAgent
  const isMac = /mac/i.test(platform) || (/Macintosh/.test(ua) && !/iPhone|iPad/.test(ua))

  let chip: string | null = null
  try {
    const gl = document.createElement('canvas').getContext('webgl')
    const ext = gl?.getExtension('WEBGL_debug_renderer_info')
    const renderer = ext ? String(gl?.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : ''
    const m = renderer.match(/Apple (M\d[^,)]*)/)
    if (m) chip = `Apple ${m[1].trim()}`
  } catch {
    chip = null
  }

  const cores = nav.hardwareConcurrency || null
  const chipMin = chip ? minMemoryForChip(chip) : null
  const browserMem = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null
  const minMemoryGb = chipMin ?? browserMem
  const memorySource = chipMin !== null ? 'chip' : browserMem !== null ? 'browser' : null
  const verdict: Verdict = !isMac ? 'no' : chip ? 'ready' : 'probably'
  return { verdict, isMac, chip, cores, minMemoryGb, memorySource }
}

function memoryLabel(r: Report) {
  if (r.minMemoryGb === null) return 'not readable from a browser; the setup wizard checks it'
  if (r.memorySource === 'chip') return `at least ${r.minMemoryGb} GB (smallest ${r.chip} configuration)`
  return `${r.minMemoryGb} GB or more (browsers report no higher)`
}

/** Minimum memory per model, from the desktop app's catalog (minRamGb). */
function modelsFor(minGb: number | null) {
  if (minGb === null) return 'SD 1.5 from 8 GB · SDXL from 12 GB · FLUX.2 klein from 16 GB (8 GB quantised) · Z-Image Turbo, Wan 2.2 video and ACE-Step music from 24 GB'
  if (minGb >= 24) return 'Z-Image Turbo, FLUX.2 klein, Wan 2.2 video and ACE-Step music all fit'
  if (minGb >= 16) return 'FLUX.2 klein, SDXL and SD 1.5 fit; Z-Image Turbo full size, video and music need 24 GB'
  if (minGb >= 8) return 'SD 1.5, the small tools and quantised FLUX.2 klein fit; SDXL from 12 GB, full FLUX.2 klein from 16 GB'
  return 'below the 8 GB Genie needs for SD 1.5'
}

const VERDICT: Record<Verdict, { title: string; body: string }> = {
  ready: { title: 'Ready to run', body: 'This is an Apple Silicon Mac. Download and the setup wizard picks models that fit.' },
  probably: {
    title: 'Probably ready',
    body: 'This is a Mac, but this browser hides the chip. Genie needs Apple Silicon (M1 or later): check Apple menu → About This Mac.',
  },
  no: { title: 'Not this machine', body: 'The Genie desktop app needs an Apple Silicon Mac. Windows is on the list.' },
}

export function CompatCheck() {
  const [report, setReport] = useState<Report | null>(null)
  const run = useCallback(() => setReport(inspect()), [])

  // Arriving at the #compat anchor (footer link, shared URL) runs the check
  // straight away, so the panel never lands empty.
  useEffect(() => {
    const onHash = () => { if (window.location.hash === COMPAT_ANCHOR) run() }
    onHash()
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [run])

  const facts = report
    ? [
        report.isMac ? 'macOS' : 'not macOS',
        report.chip ?? (report.isMac ? 'chip hidden by this browser' : null),
        report.cores ? `${report.cores} cores` : null,
        memoryLabel(report),
        modelsFor(report.minMemoryGb),
      ].filter((f): f is string => Boolean(f))
    : []

  return (
    <div
      id="compat"
      className="mt-3 flex scroll-mt-24 flex-col gap-4 rounded-[3px] border border-rule bg-card px-6 py-5 md:flex-row md:items-center md:gap-8"
    >
      <span className="eyebrow shrink-0 md:w-[200px]">Will it run on my Mac?</span>
      <div className="min-w-0 flex-1 text-[14px] leading-relaxed text-ink-2">
        {report ? (
          <>
            <span className="inline-flex items-center gap-2 align-baseline">
              <span
                className={`inline-block size-2.5 rounded-full ${report.verdict === 'no' ? 'border border-rule-strong' : 'bg-lamp'}`}
                aria-hidden
              />
              <span className="text-[18px] leading-none tracking-[-0.01em] text-ink">{VERDICT[report.verdict].title}.</span>
            </span>{' '}
            {report.verdict === 'probably' && <>{VERDICT.probably.body} </>}
            <span className="font-mono text-[12px]">{facts.join(' · ')}</span>
            {report.verdict === 'no' && (
              <>
                {' '}
                <a href={WAITLIST_PATH} className="text-ink underline decoration-rule-strong underline-offset-4">
                  Windows: get notified
                </a>
              </>
            )}
          </>
        ) : (
          <>Apple Silicon (M1 or later) and 8 GB of memory for the smallest models; 16 GB or more for most. Checked in your browser; nothing is sent anywhere.</>
        )}
      </div>
      <button type="button" onClick={run} className={actionClass(report ? 'outline' : 'solid', 'sm', 'shrink-0 self-start md:self-auto')}>
        {report ? 'Check again' : 'Check this Mac'}
      </button>
    </div>
  )
}
